import { accountStatus } from './balance';
import { CashFlowActions } from './CashFlows';
import { formatMoney, netPnl, usd } from './pnl';
import { currentLocale } from './langState';
import { useI18n } from './i18n';

const RISK_LABEL = { safe: 'Safe', warning: 'Getting close', danger: 'Danger', blown: 'Max loss hit' };
export const OVERALL_LABEL = { safe: 'All clear', warning: 'Caution', danger: 'At risk', blown: 'Account failed' };

export function OverallBadge({ status }) {
  const { t } = useI18n();
  if (!status || !status.overall) return null;
  return <span className={`overall-badge risk-${status.overall}`}>{t(OVERALL_LABEL[status.overall])}</span>;
}

export function ConsistencyLine({ status }) {
  const { t, tr } = useI18n();
  if (!status || !status.consistencyLimit) return null;
  const base = status.consistencyBase === 'target' ? t('your profit target') : t('total profit');
  if (status.consistencyPct === null) {
    return <>{t('Limit {limit}% of {base}; no winning day yet.', { limit: status.consistencyLimit, base })}</>;
  }
  return (
    <>
      {tr('Your best day ({amount}) is <0>{pct}%</0> of {base} (limit {limit}%).', [
        (x) => <strong className={status.consistencyOk ? 'gain' : 'risk-text-warning'}>{x}</strong>,
      ], { amount: usd.format(status.bestDay), pct: Math.round(status.consistencyPct), base, limit: status.consistencyLimit })}
    </>
  );
}

function money(n) {
  return usd.format(n);
}

// When the account's numbers last changed: the newest trade added to the journal
// (an import or a manual entry). A failed import adds nothing, so it never looks fresh.
export function lastRecorded(trades) {
  const times = trades.map((x) => new Date(x.createdAt || 0).getTime()).filter((n) => n > 0);
  return times.length ? new Date(Math.max(...times)) : null;
}

function recordedLabel(date) {
  return date.toLocaleString(currentLocale(), { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

// Where the numbers come from, shown on the rules panel.
export function FreshnessNote({ trades, short = false }) {
  const { t } = useI18n();
  const last = lastRecorded(trades);
  if (short) {
    return (
      <p className="freshness-note">
        {last ? t('Updated {time} · recorded trades only', { time: recordedLabel(last) }) : t('No trades recorded yet')}
      </p>
    );
  }
  return (
    <p className="freshness-note">
      {last
        ? t('Based on recorded trades · Last updated {time} · Open positions not included.', { time: recordedLabel(last) })
        : t('Based on recorded trades · No trades recorded yet · Open positions not included.')}{' '}
      {t("Market Matrix can't block trades; your firm's platform enforces its rules.")}
    </p>
  );
}

// Detailed balance card for the selected account.
export function AccountStatusCard({ account, trades, onManage, onAccountChange, onSessionExpired }) {
  const { t, tr } = useI18n();
  const s = accountStatus(account, trades);
  if (!s) {
    return (
      <section className="account-status empty-status">
        <p>
          {tr('Add a <0>starting balance</0> for {name} to track its balance, and a profit target and max loss limit to see how close you are to passing or blowing it.',
            [(x) => <strong>{x}</strong>], { name: account.name })}
        </p>
        <button type="button" className="secondary small" onClick={onManage}>{t('Set up account')}</button>
      </section>
    );
  }
  const pct = (s.change / s.start) * 100;

  return (
    <section className={`account-status${s.risk ? ` risk-${s.risk}` : ''}`}>
      <div className="status-balance">
        <span className="stat-label">
          {account.attempt > 1
            ? t('{name} · Attempt {n} balance', { name: account.name, n: account.attempt })
            : t('{name} balance', { name: account.name })} <OverallBadge status={s} />
        </span>
        <span className="status-balance-value">{money(s.balance)}</span>
        <span className={s.change >= 0 ? 'gain' : 'loss'}>
          {t('{change} ({pct}) since starting at {start}', { change: formatMoney(s.change), pct: `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%`, start: money(s.start) })}
        </span>
        {onAccountChange && (
          <CashFlowActions account={account} status={s} onChange={onAccountChange} onSessionExpired={onSessionExpired} />
        )}
      </div>

      {(s.target !== undefined || s.floor !== undefined || s.consistencyLimit || s.dailyLeft !== undefined) && (
        <div className="status-rules">
          {s.target !== undefined && (
            <div className="status-rule">
              <div className="status-rule-head">
                <span className="stat-label">{t('Profit target')}</span>
                <span className="status-rule-value">
                  {s.toPass <= 0 ? (
                    <span className="gain">{t('Target reached')}</span>
                  ) : (
                    <>{tr("You're <0>{amount}</0> from passing", [(x) => <strong className="gain">{x}</strong>], { amount: money(s.toPass) })}</>
                  )}
                </span>
              </div>
              <div className="status-bar" role="img" aria-label={t('{pct}% of profit target', { pct: Math.round(s.progress * 100) })}>
                <span className="status-bar-fill target" style={{ width: `${s.progress * 100}%` }} />
              </div>
              <span className="status-rule-note">
                {t('{made} of {needed} • target balance {target}', { made: money(Math.max(0, s.change)), needed: money(s.target - s.start), target: money(s.target) })}
                {s.raisedTarget && ` ${t('(raised by the consistency rule)')}`}
              </span>
            </div>
          )}
          {s.floor !== undefined && (
            <div className="status-rule">
              <div className="status-rule-head">
                <span className="stat-label">{t('Max loss limit')}</span>
                <span className="status-rule-value">
                  {s.risk === 'blown' ? (
                    <span className="loss">{t('Max loss level reached')}</span>
                  ) : account.drawdownType === 'trailing_intraday' ? (
                    <>{tr("You're about <0>{amount}</0> from blowing the account", [(x) => <strong className={`risk-text-${s.risk}`}>{x}</strong>], { amount: money(s.toBlow) })}</>
                  ) : (
                    <>{tr("You're <0>{amount}</0> from blowing the account", [(x) => <strong className={`risk-text-${s.risk}`}>{x}</strong>], { amount: money(s.toBlow) })}</>
                  )}
                </span>
              </div>
              <div className="status-bar" role="img" aria-label={t('{amount} above max loss level', { amount: money(s.toBlow) })}>
                <span className={`status-bar-fill risk-${s.risk}`}
                  style={{ width: `${Math.max(0, Math.min(1, s.toBlow / account.maxLossLimit)) * 100}%` }} />
              </div>
              <span className="status-rule-note">
                <span className={`risk-badge risk-${s.risk}`}>{t(RISK_LABEL[s.risk])}</span>
                {account.drawdownType === 'trailing_intraday'
                  ? <>{t('Estimated max loss level {amount}', { amount: money(s.floor) })}{' '}{t('(an estimate: it trails after every closed trade, but your firm also counts open-trade highs that aren\u2019t in your file, so the real level can be higher; keep extra room)')}</>
                  : <>{t('Max loss level {amount}', { amount: money(s.floor) })}{' '}{account.drawdownType === 'static' ? t('(static)') : t('(trails your end-of-day high)')}</>}
              </span>
            </div>
          )}
          {s.consistencyLimit && (
            <div className="status-rule">
              <div className="status-rule-head">
                <span className="stat-label">{t('Consistency rule')}</span>
                <span className="status-rule-value"><ConsistencyLine status={s} /></span>
              </div>
            </div>
          )}
          {s.dailyLeft !== undefined && (
            <div className="status-rule">
              <div className="status-rule-head">
                <span className="stat-label">{t('Daily loss limit')}</span>
                <span className="status-rule-value">
                  {s.dailyLeft <= 0 ? (
                    <span className="loss">{t('Daily limit reached, stop for today')}</span>
                  ) : (
                    <>{tr('You can lose <0>{amount}</0> more today', [(x) => <strong>{x}</strong>], { amount: money(s.dailyLeft) })}</>
                  )}
                </span>
              </div>
            </div>
          )}
        </div>
      )}
      <FreshnessNote trades={trades} />
    </section>
  );
}

// One compact card per account, shown under "All accounts".
export function AccountOverview({ accounts, trades, onSelect }) {
  const { t } = useI18n();
  // Accounts without a starting balance (e.g. a stock account) show their net P&L instead.
  const cards = accounts
    .filter((a) => !a.archived)
    .map((a) => {
      const list = trades.filter((x) => x.accountId === a._id);
      return { account: a, status: accountStatus(a, list), net: list.reduce((sum, x) => sum + netPnl(x), 0) };
    });
  if (!cards.length) return null;

  return (
    <section className={`account-overview overview-count-${Math.min(cards.length, 5)}`}>
      {cards.map(({ account, status: s, net }) => (!s ? (
        <button key={account._id} type="button" className="overview-card" onClick={() => onSelect(account._id)}>
          <span className="overview-name">{account.name}</span>
          <span className={`overview-balance ${net >= 0 ? 'gain' : 'loss'}`}>{formatMoney(net)}</span>
          <span className="overview-line">{t('Net P&L')}</span>
        </button>
      ) : (
        <button key={account._id} type="button" className={`overview-card${s.risk ? ` risk-${s.risk}` : ''}`}
          onClick={() => onSelect(account._id)}>
          <span className="overview-name">{account.name}</span>
          <span className="overview-balance">{money(s.balance)}</span>
          <span className={`overview-change ${s.change >= 0 ? 'gain' : 'loss'}`}>{formatMoney(s.change)}</span>
          {s.toPass !== undefined && (
            <span className="overview-line">
              {s.toPass <= 0 ? t('Target reached') : t('{amount} to pass', { amount: money(s.toPass) })}
            </span>
          )}
          {s.toBlow !== undefined && (
            <span className={`overview-line risk-text-${s.risk}`}>
              {s.risk === 'blown' ? t('Max loss hit') : t('{amount} from max loss', { amount: money(s.toBlow) })}
            </span>
          )}
        </button>
      )))}
    </section>
  );
}
